import { useMemo } from "react";
import { usePermissions, ROLES } from "../../../context/PermissionsContext/PermissionsContext";
import useAuthUser from "../../../hooks/useAuthUser";

// Role keys del catálogo backend que corresponden al equipo de billing /
// revenue cycle. Aterrizan en el dashboard de Billing & Revenue Integrity.
// (El tier mapeado de coder_billing es PCP por falta de un tier dedicado; aquí
// lo detectamos por roleKey para darles su dashboard sin tocar el RBAC.)
const BILLING_ROLE_KEYS = new Set(["coder_billing", "billing", "biller"]);

/**
 * Hook para determinar qué dashboard mostrar según el rol del usuario
 *
 * Mapeo de roles a dashboards:
 * - CEO -> "ceo" (Executive Dashboard)
 * - Admin -> "admin" (System Administration)
 * - Care Management -> "care_manager" (Care Management Dashboard)
 * - Behavioral Health -> "behavioral_health" (BH Dashboard)
 * - PCP / default -> "pcp" (Clinical Dashboard)
 */
export default function useDashboardRouter() {
  const { userRole } = usePermissions();
  const user = useAuthUser();
  const roleKey = String(user?.activeRole?.key || "").toLowerCase();

  const dashboardType = useMemo(() => {
    // El equipo de billing aterriza en su dashboard, detectado por roleKey real.
    if (BILLING_ROLE_KEYS.has(roleKey)) return "billing";

    switch (userRole) {
      case ROLES.CEO:
        return "ceo";
      case ROLES.ADMIN:
        return "admin";
      case ROLES.CARE_MANAGEMENT:
        return "care_manager";
      case ROLES.BEHAVIORAL_HEALTH:
        return "behavioral_health";
      case ROLES.GUEST:
        // Empleados sin perfil ejecutivo/clínico/care-manager (recepción,
        // administración, back office, facturación, IT, invitados...) reciben
        // el dashboard genérico de espacio de trabajo personal.
        return "employee";
      case ROLES.PCP:
        return "pcp";
      default:
        return "pcp";
    }
  }, [userRole, roleKey]);

  const dashboardTitle = useMemo(() => {
    switch (dashboardType) {
      case "billing":
        return "Billing & Revenue Integrity";
      case "ceo":
        return "Executive Dashboard";
      case "admin":
        return "System Administration";
      case "care_manager":
        return "Care Management Dashboard";
      case "behavioral_health":
        return "Behavioral Health Dashboard";
      case "employee":
        return "My Workspace";
      case "pcp":
      default:
        return "Clinical Dashboard";
    }
  }, [dashboardType]);

  return {
    dashboardType,
    dashboardTitle,
    userRole,
  };
}
